import { useEffect, useRef } from 'react';
import { setAppInsightsUser, trackPageView as trackAppInsightsPageView } from '@/lib/applicationInsights';
import { useAuth } from '@/lib/auth/hooks/auth-provider';
import { router } from '@/src/router/routes';

type PageViewTelemetry = Parameters<typeof trackAppInsightsPageView>[0];
type PageLocation = Pick<typeof router.state.location, 'href' | 'pathname'>;

type PageViewRecord = {
  telemetry: PageViewTelemetry;
  pathname: string;
  href: string;
};

// A record that has already been sent, plus when it was sent (used to fold a
// late same-path sync into the page the user just landed on).
type TrackedPageView = PageViewRecord & { emittedAt: number };

// Pages sync UI state into the URL on load via `navigate({ replace: true })`
// (e.g. overview -> ?stage=early, deadlines -> ?taskId=...). That fires a
// resolved-route event a few hundred ms after the initial view, for the same
// pathname. Rather than emit two page views (or record the transient URL the
// user never really lands on), we hold a page view for this window and adopt the
// settled URL before sending a single page view. The buffer is flushed early on
// a genuine navigation (different pathname) and on tab hide/unmount so nothing
// is lost. The initial view keeps its navigation-timing data through the swap.
const SETTLE_WINDOW_MS = 1000;

export function useAppInsights() {
  const { user, isLoading } = useAuth();
  const authLoadingRef = useRef(isLoading);
  const lastTrackedRef = useRef<TrackedPageView | null>(null);
  const pendingPageViewsRef = useRef<PageViewTelemetry[]>([]);
  const bufferedViewRef = useRef<PageViewRecord | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    authLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setAppInsightsUser(user?.email);

    if (pendingPageViewsRef.current.length === 0) {
      return;
    }

    const pendingPageViews = pendingPageViewsRef.current;
    pendingPageViewsRef.current = [];
    pendingPageViews.forEach((pageView) => {
      trackAppInsightsPageView(pageView);
    });
  }, [isLoading, user?.email]);

  useEffect(() => {
    const sendOrQueuePageView = (pageView: PageViewTelemetry) => {
      if (authLoadingRef.current) {
        pendingPageViewsRef.current.push(pageView);
        return;
      }

      trackAppInsightsPageView(pageView);
    };

    const clearSettleTimer = () => {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
    };

    const emitBufferedView = () => {
      clearSettleTimer();
      const buffered = bufferedViewRef.current;
      bufferedViewRef.current = null;
      if (!buffered) {
        return;
      }

      lastTrackedRef.current = { ...buffered, emittedAt: Date.now() };
      sendOrQueuePageView(buffered.telemetry);
    };

    const trackPageView = (location: PageLocation, isInitialPageView = false) => {
      const { href, pathname } = location;

      if (lastTrackedRef.current?.href === href) {
        return;
      }

      const buffered = bufferedViewRef.current;
      if (buffered) {
        if (buffered.pathname === pathname) {
          // Same logical page settling into its canonical URL (e.g. ?stage=early).
          // Adopt the settled URL but keep the buffered view's timing and refUri.
          buffered.telemetry = { ...buffered.telemetry, uri: href };
          buffered.href = href;
          return;
        }
        // Genuine navigation to a different page — flush what we were holding.
        emitBufferedView();
      }

      // A same-path sync that arrives just after the buffered view was flushed
      // (e.g. a slow settle): fold it into that page instead of re-tracking.
      const lastTracked = lastTrackedRef.current;
      if (
        lastTracked !== null &&
        lastTracked.pathname === pathname &&
        Date.now() - lastTracked.emittedAt < SETTLE_WINDOW_MS
      ) {
        lastTracked.href = href;
        return;
      }

      bufferedViewRef.current = {
        pathname,
        href,
        telemetry: {
          name: pathname,
          uri: href,
          refUri: lastTracked?.href ?? undefined,
          startTime: new Date(),
          ...(isInitialPageView ? {} : { properties: { duration: 0 } }),
        },
      };
      clearSettleTimer();
      settleTimerRef.current = setTimeout(emitBufferedView, SETTLE_WINDOW_MS);
    };

    // Use TanStack's parsed location for both initial and resolved route views
    // so duplicate detection compares one URL format.
    trackPageView(router.state.location, true);

    // Subscribe to route changes — fires after the new route resolves and renders.
    const unsubscribe = router.subscribe('onResolved', ({ toLocation }) => {
      trackPageView(toLocation);
    });

    // Flush any held page view before the tab is hidden/closed so it isn't lost.
    const flushOnHidden = () => {
      if (document.visibilityState === 'hidden') {
        emitBufferedView();
      }
    };
    window.addEventListener('pagehide', emitBufferedView);
    document.addEventListener('visibilitychange', flushOnHidden);

    return () => {
      unsubscribe();
      window.removeEventListener('pagehide', emitBufferedView);
      document.removeEventListener('visibilitychange', flushOnHidden);
      emitBufferedView();
    };
  }, []);
}
